import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import EmployeeSelector from '../../events/EmployeeSelector'
import { apiError } from './dopsShared'

// Ask a colleague to assess a procedure from an ePCR entry (POST /dops).
// Reusable from the ePCR editor:
//   <RequestDopsDialog open epcrID entryId section="treatment"
//     procedureLabel="Intubation" onClose={() => ...} onRequested={(dops) => ...} />
// `section` is 'treatment' or 'assess' (the module list the entry lives in).
const RequestDopsDialog = ({
  open,
  onClose,
  epcrID,
  entryId,
  section = 'treatment',
  procedureLabel,
  onRequested,
}) => {
  const { setToast } = useContext(GlobalContext)
  const [assessor, setAssessor] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setAssessor(null)
    setMessage('')
    setError('')
  }, [open, entryId])

  const submit = () => {
    setBusy(true)
    setError('')
    API.post('/dops', {
      epcrID,
      entryId,
      section,
      assessor: assessor?._id,
      message: message.trim() || undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `DOPS requested from ${assessor.firstName ?? ''} ${assessor.lastName ?? ''}`.trim(),
          })
          onRequested?.(res.data)
          onClose()
        } else {
          setError(apiError(res, 'Unable to request a DOPS'))
        }
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>
        Request a DOPS
        {procedureLabel && (
          <Typography variant="body2" color="text.secondary">
            {procedureLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={0.5}>
          <Typography variant="body2" color="text.secondary">
            Ask a colleague who saw you perform this procedure to complete a
            Direct Observation of Procedural Skills. They will see the
            clinical details of the intervention only - no patient
            identifiers.
          </Typography>
          <EmployeeSelector
            label="Assessor"
            value={assessor}
            onChange={(e, user) => setAssessor(user)}
            preventSelectingSelf
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Message (optional)"
            placeholder="e.g. Thanks for supervising today - could you assess my intubation?"
            size="small"
            multiline
            minRows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!assessor?._id || !epcrID || !entryId || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Send request
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RequestDopsDialog
